import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';

const CopilotContext = createContext();

// Separate context for design data - can be used by child components like Chart
const CopilotDesignContext = createContext();
export const useCopilotDesign = () => useContext(CopilotDesignContext);
export { CopilotDesignContext };

const COPILOT_EXPANDED_KEY = 'copilot_expanded';
const COPILOT_DISPLAY_MODE_KEY = 'copilot_display_mode';

export const DisplayMode = {
  REGULAR: 'regular',
  DEBUG: 'debug',
};

export const CopilotProvider = ({ children }) => {
  const [copilotOpen, setCopilotOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('copilot_open_status');
      return saved ? JSON.parse(saved) : false;
    }
    return false;
  });

  const [isExpanded, setIsExpanded] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(COPILOT_EXPANDED_KEY);
      return saved ? JSON.parse(saved) : false;
    }
    return false;
  });

  const [displayMode, setDisplayMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(COPILOT_DISPLAY_MODE_KEY);
      return saved || DisplayMode.REGULAR;
    }
    return DisplayMode.REGULAR;
  });

  const [apis, setApis] = useState({
    gridApi: null,
  });

  // Persist copilotOpen state to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('copilot_open_status', JSON.stringify(copilotOpen));
    }
  }, [copilotOpen]);

  // Persist isExpanded state to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(COPILOT_EXPANDED_KEY, JSON.stringify(isExpanded));
    }
  }, [isExpanded]);

  // Persist displayMode state to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(COPILOT_DISPLAY_MODE_KEY, displayMode);
    }
  }, [displayMode]);

  const toggleCopilot = () => setCopilotOpen(!copilotOpen);
  const toggleExpand = () => setIsExpanded(!isExpanded);

  const registerApi = useCallback((apiName, apiInstance) => {
    setApis((prevApis) => {
      // Skip if already registered with same instance
      if (prevApis[apiName] === apiInstance) {
        return prevApis;
      }
      return {
        ...prevApis,
        [apiName]: apiInstance,
      };
    });
  }, []);

  const getApi = useCallback(
    (apiName) => {
      return apis[apiName];
    },
    [apis]
  );

  const hasApi = useCallback(
    (apiName) => {
      return !!apis[apiName];
    },
    [apis]
  );

  return (
    <CopilotContext.Provider
      value={{
        // UI state
        copilotOpen,
        setCopilotOpen,
        toggleCopilot,
        isExpanded,
        toggleExpand,
        displayMode,
        setDisplayMode,

        // Data APIs
        apis,
        registerApi,
        getApi,
        hasApi,
      }}
    >
      {children}
    </CopilotContext.Provider>
  );
};

export const useCopilot = () => {
  const context = useContext(CopilotContext);
  if (!context) {
    throw new Error('useCopilot must be used within a CopilotProvider');
  }
  return context;
};
